// 系统设置 / 报表 / 淘宝店共用的页面骨架与小组件。
// 现有 src/components 里没有这些（导航栏、分组列表、通用表格、翻页、底部 sheet），先写在这里，由主会话统一提取。
import { useEffect, useState, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router';
import { Icon } from '@/components/Icon';
import { legacyHref } from '@/shell/nav';
import { useBack } from './hooks';
import s from './Kit.module.css';

/* ------------------------------ 手机导航栏 ------------------------------ */

/**
 * 手机导航栏。
 * - large：根页（Tab 页或从「应用」进入的一级页）大标题 24/600，左对齐；
 * - 否则：二级页，左上返回 + 17 居中标题。
 */
export function PhoneBar({ title, sub, back, backLabel, right, large }: { title: ReactNode; sub?: ReactNode; back?: string; backLabel?: string; right?: ReactNode; large?: boolean }) {
  const goBack = useBack(back || '/');
  if (large) {
    return (
      <header className={s.pbLarge}>
        {back && (
          <button className={s.pbBackRow} onClick={goBack}>
            <Icon name="back" size={18} />{backLabel || '返回'}
          </button>
        )}
        <div className={s.pbTitleRow}>
          <h1>{title}</h1>
          {right && <div className={s.pbRight}>{right}</div>}
        </div>
        {sub && <p className={s.pbSub}>{sub}</p>}
      </header>
    );
  }
  return (
    <header className={s.pb}>
      <button className={s.pbBack} onClick={goBack} aria-label="返回">
        <Icon name="back" size={20} />
        {backLabel && <span>{backLabel}</span>}
      </button>
      <div className={s.pbMid}>
        <b>{title}</b>
        {sub && <small>{sub}</small>}
      </div>
      <div className={s.pbRight}>{right}</div>
    </header>
  );
}

/* ------------------------------ 桌面 / iPad 页头 ------------------------------ */

export function DeskHead({ title, count, sub, children, leading, className }: { title: ReactNode; count?: ReactNode; sub?: ReactNode; children?: ReactNode; leading?: ReactNode; className?: string }) {
  return (
    <header className={[s.dh, className].filter(Boolean).join(' ')}>
      {leading && <span className={s.dhLead}>{leading}</span>}
      <div className={s.dhT}>
        <h1>{title}{count != null && <small className="tnum">{count}</small>}</h1>
        {sub && <p>{sub}</p>}
      </div>
      <span className={s.sp} />
      {children}
    </header>
  );
}

/* ------------------------------ chips / 搜索 ------------------------------ */

export type ChipItem = { key: string; label: ReactNode; count?: number | null };

/** 横滑 chips：视图或单选筛选；计数为 null / undefined 时不显示 */
export function Chips({ items, value, onChange, className }: { items: ChipItem[]; value: string; onChange: (k: string) => void; className?: string }) {
  return (
    <div className={[s.chips, className].filter(Boolean).join(' ')} role="tablist">
      {items.map((c) => (
        <button key={c.key} role="tab" aria-selected={c.key === value} className={`${s.chip} ${c.key === value ? s.on : ''}`} onClick={() => onChange(c.key)}>
          {c.label}
          {c.count != null && <i className="tnum">{c.count}</i>}
        </button>
      ))}
    </div>
  );
}

/** 分段切换（页签）：角色 / 账号，订单 / 出库 / 退货 */
export function Segmented({ items, value, onChange }: { items: ChipItem[]; value: string; onChange: (k: string) => void }) {
  return (
    <div className={s.seg} role="tablist">
      {items.map((c) => (
        <button key={c.key} role="tab" aria-selected={c.key === value} className={c.key === value ? s.segOn : ''} onClick={() => onChange(c.key)}>
          {c.label}
          {c.count != null && <i className="tnum">{c.count}</i>}
        </button>
      ))}
    </div>
  );
}

export function SearchBox({ value, onSearch, placeholder, className }: { value: string; onSearch: (q: string) => void; placeholder: string; className?: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <form
      className={[s.search, className].filter(Boolean).join(' ')}
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onSearch(draft.trim());
      }}
    >
      <Icon name="srch" />
      <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} enterKeyHint="search" />
      {draft && (
        <button type="button" className={s.clearQ} aria-label="清空" onClick={() => { setDraft(''); onSearch(''); }}>
          <Icon name="x" size={14} />
        </button>
      )}
    </form>
  );
}

/* ------------------------------ 表格 / 翻页 ------------------------------ */

export type Col<T> = { key: string; title: ReactNode; w?: number; align?: 'r' | 'c'; fix?: boolean; fixRight?: boolean; render: (row: T) => ReactNode };

/** 通用只读表格：首列可吸左，数字右对齐（文档 18 §5） */
export function DataTable<T>({ cols, rows, rowKey, onRow, selectedKey, dense, minWidth }: { cols: Col<T>[]; rows: T[]; rowKey: (r: T) => string; onRow?: (r: T) => void; selectedKey?: string; dense?: boolean; minWidth?: number }) {
  return (
    <div className={s.tscroll}>
      <table className={`${s.tbl} ${dense ? s.dense : ''} ${onRow ? s.click : ''}`} style={minWidth ? { minWidth } : undefined}>
        <colgroup>{cols.map((c) => <col key={c.key} style={c.w ? { width: c.w } : undefined} />)}</colgroup>
        <thead>
          <tr>{cols.map((c) => <th key={c.key} className={[c.fix && s.fix, c.fixRight && s.fixR, c.align === 'r' && s.r, c.align === 'c' && s.center].filter(Boolean).join(' ')}>{c.title}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const k = rowKey(r);
            return (
              <tr key={k} data-row-key={k} className={k === selectedKey ? s.sel : undefined} onClick={onRow ? () => onRow(r) : undefined}>
                {cols.map((c) => <td key={c.key} className={[c.fix && s.fix, c.fixRight && s.fixR, c.align === 'r' && s.r, c.align === 'c' && s.center].filter(Boolean).join(' ')}>{c.render(r)}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function Pager({ page, pageSize, count, total, hasNext, onGoto }: { page: number; pageSize: number; count: number; total: number; hasNext: boolean; onGoto: (p: number) => void }) {
  const from = total ? (page - 1) * pageSize + 1 : 0;
  return (
    <footer className={s.pager}>
      <span className="tnum">第 {from}–{(page - 1) * pageSize + count} 条 / 共 {total} 条</span>
      <span className={s.sp} />
      <button className={s.pbtn} disabled={page <= 1} onClick={() => onGoto(page - 1)}>上一页</button>
      <span className="tnum">{page}</span>
      <button className={s.pbtn} disabled={!hasNext} onClick={() => onGoto(page + 1)}>下一页</button>
    </footer>
  );
}

/** 卡片列表底部：加载更多的哨兵 */
export function MoreHint({ hasNext, loading, count, sentinel }: { hasNext: boolean; loading: boolean; count: number; sentinel: RefObject<HTMLDivElement | null> }) {
  return (
    <div ref={sentinel} className={s.more}>
      {hasNext ? (loading ? '加载中…' : '上滑加载更多') : count > 0 ? '没有更多了' : ''}
    </div>
  );
}

/* ------------------------------ iOS 分组列表（手机设置页） ------------------------------ */

export function Group({ title, extra, note, children }: { title?: ReactNode; extra?: ReactNode; note?: ReactNode; children: ReactNode }) {
  return (
    <section className={s.group}>
      {(title || extra) && <h3 className={s.gh}><span>{title}</span>{extra}</h3>}
      <div className={s.glist}>{children}</div>
      {note && <p className={s.gnote}>{note}</p>}
    </section>
  );
}

/** 一行：标签左、值右；有 to / href 时带箭头 */
export function Cell({ icon, tone, label, hint, value, to, href }: { icon?: string; tone?: string; label: ReactNode; hint?: ReactNode; value?: ReactNode; to?: string; href?: string }) {
  const inner = (
    <>
      {icon && <span className={`${s.cico} ${tone ? s[`t_${tone}`] : ''}`}><Icon name={icon} size={18} /></span>}
      <span className={s.clab}>
        <b>{label}</b>
        {hint && <small>{hint}</small>}
      </span>
      {value != null && <span className={s.cval}>{value}</span>}
      {(to || href) && <Icon name="chev" className={s.cchev} />}
    </>
  );
  if (to) return <Link to={to} className={s.cell}>{inner}</Link>;
  if (href) return <a href={href} className={s.cell}>{inner}</a>;
  return <div className={s.cell}>{inner}</div>;
}

/* ------------------------------ 面板 / 键值 ------------------------------ */

export function Panel({ title, extra, children, flush }: { title?: ReactNode; extra?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <section className={s.panel}>
      {(title || extra) && (
        <header className={s.ph}>
          <h3>{title}</h3>
          {extra}
        </header>
      )}
      <div className={flush ? undefined : s.pbody}>{children}</div>
    </section>
  );
}

export function KV({ items }: { items: ([ReactNode, ReactNode] | false | null | undefined)[] }) {
  return (
    <dl className={s.kv}>
      {items.filter(Boolean).map((it, i) => {
        const [k, v] = it as [ReactNode, ReactNode];
        return <div key={i}><dt>{k}</dt><dd>{v}</dd></div>;
      })}
    </dl>
  );
}

/* ------------------------------ 老版入口 / 只读提示 ------------------------------ */

/** 写操作一律去老版（只读期） */
export function LegacyLink({ path, children, primary }: { path: string; children?: ReactNode; primary?: boolean }) {
  return (
    <a className={`${s.legacy} ${primary ? s.legacyPri : ''}`} href={legacyHref(path)} title="只读体验期：写操作在老版进行">
      {children || '在老版中操作'}
    </a>
  );
}

export function ReadonlyNote({ children }: { children?: ReactNode }) {
  return <p className={s.note}>{children || '新版只读体验期：新增、修改、删除请点「在老版中操作」，登录状态共用。'}</p>;
}

/* ------------------------------ 底部 sheet / 右侧抽屉 ------------------------------ */

/** 筛选等短表单：手机是底部 sheet，iPad / 桌面是右侧 400 抽屉（文档 23 §6） */
export function Sheet({ title, sheet, onClose, onReset, footer, children }: { title: ReactNode; sheet: boolean; onClose: () => void; onReset?: () => void; footer?: ReactNode; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className={s.scrim} onClick={onClose}>
      <aside className={sheet ? s.sheet : s.drawer} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={typeof title === 'string' ? title : undefined}>
        {sheet && <div className={s.grab} />}
        <header className={s.shd}>
          <button className={s.shx} onClick={onClose} aria-label="关闭"><Icon name="x" size={20} /></button>
          <b>{title}</b>
          {onReset ? <button className={s.shr} onClick={onReset}>重置</button> : <span />}
        </header>
        <div className={s.sbd}>{children}</div>
        {footer && <footer className={s.sft}>{footer}</footer>}
      </aside>
    </div>
  );
}

/** sheet 里的一组单选 chips */
export function ChoiceGroup({ title, options, value, onChange }: { title: ReactNode; options: { value: string; label: ReactNode }[]; value?: string; onChange: (v?: string) => void }) {
  return (
    <section className={s.cg}>
      <h4>{title}</h4>
      <div className={s.cgc}>
        {options.map((o) => (
          <button key={o.value} className={`${s.cgi} ${value === o.value ? s.cgon : ''}`} onClick={() => onChange(value === o.value ? undefined : o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------ 数字瓦片 ------------------------------ */

export function Tiles({ children }: { children: ReactNode }) {
  return <div className={s.tiles}>{children}</div>;
}

export function Tile({ label, value, sub, tone, title }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: 'bad' | 'warn' | 'good'; title?: string }) {
  return (
    <div className={s.tile} title={title}>
      <span>{label}</span>
      <b className={tone ? s[tone] : undefined}>{value}</b>
      {sub && <small>{sub}</small>}
    </div>
  );
}
